/**
 * livelevels.ts — ONE price model for the LIVE position's entry / SL / TP / LIQ,
 * keyed by position id, that every pane renders from.
 *
 * THE DECISION, SO IT IS NOT RE-LITIGATED
 * ---------------------------------------
 * Option (a) — one shared price model, N read-only renderers — is adopted. The
 * rejected option (b) was per-pane primitives holding their own copy of the stop and
 * synced by events: a stop is ONE price on ONE position, so four copies are four
 * chances to disagree, and the drift only shows up as two panes drawing a stop at two
 * prices while the exchange holds a third. The same second-source-of-truth bug this
 * app has already paid for twice (per-pane `markerState`, `Position.stop_loss`).
 * A pane therefore owns a SUBSCRIPTION, never a price.
 *
 * PRICES ONLY, NEVER PIXELS. Panes have independent price windows (`main.ts:419`), so
 * a shared y-coordinate or price scale would be wrong on three panes out of four.
 * Every pane converts price -> y with its own scale, inside its own primitive.
 *
 * BOUND BY POSITION ID AT CONSTRUCTION. `bindLevels` captures the id; nothing here
 * resolves a level by "visually nearest line". Andrei runs 26+ isolated-margin
 * positions and a mis-bound row is a real loss.
 *
 * COMMITTED vs PENDING. `sl`/`tp` are what the exchange holds, written only by
 * `sync()` from the account snapshot. `pendingSl`/`pendingTp` are a staged, un-sent
 * override (the drag gesture, wired by the sibling task). `effective()` prefers the
 * pending value for DRAWING; nothing here sends anything anywhere.
 *
 * LIQUIDATION IS DERIVED. It has no setter: it is a consequence of leverage and size
 * (`ticket.ts:706`), reported by the exchange, and a user-supplied liq price would be
 * a fiction drawn in the colour of a fact. `setPending` refuses it by name.
 */
import type { AccountLevel, LevelDragCallbacks } from './levelprimitive';
import { LEG_COLORS } from './ticket';
import { esc, fmtPx, type PositionRow, type ProtectionLeg, type Snapshot } from './tradepanel';

/** A live position's prices. `id` IS the snapshot's `coin` — dex-prefixed
 *  (`xyz:AMZN`), which is what makes it unique across the 10 live dexes. */
export interface LivePositionLevels {
  readonly id: string;
  readonly side: 'long' | 'short';
  readonly size: number;
  readonly mark: number | null;
  readonly entry: number | null;
  /** Derived, never user input. */
  readonly liq: number | null;
  /** Committed = on the exchange. */
  readonly sl: number | null;
  readonly tp: number | null;
  /** How many TP legs the modify path could move. `tp` above is their MINIMUM (the
   *  panel's aggregate), so with two the drawn line is not a leg the modify path can
   *  move — see `validateStaged`. This counts only TRIGGER-BEARING legs, the same
   *  predicate the server uses (`manage._is_protective_leg`); see `protectiveTpCount`
   *  for the measurement that made the distinction load-bearing. */
  readonly tpCount: number;
  /** Staged, not sent. null = no override. */
  readonly pendingSl: number | null;
  readonly pendingTp: number | null;
}

/** The legs a user may stage. `liq` is deliberately absent. */
export type PendingLeg = 'sl' | 'tp';

/**
 * The drag handle for one position's one leg — `xyz:AMZN|sl`.
 *
 * IT CARRIES THE POSITION ID, which is the whole point: the handle is minted where the
 * line is DRAWN, so the drag reports the leg it belongs to instead of the visually
 * nearest one. With 26+ isolated-margin positions whose stops sit within a few dollars
 * of each other, "nearest line" and "the line I grabbed" are different positions often
 * enough to lose money, and the mistake would look like a normal stop.
 *
 * `|` is the separator because an HL coin can contain `:` (the dex prefix) but not `|`.
 */
export const legId = (id: string, leg: PendingLeg): string => `${id}|${leg}`;

/** Split a handle back into (position id, leg), or null if it is not one of ours.
 *
 *  Returns null rather than guessing: a handle arrives from a primitive's callback,
 *  where TypeScript has stopped being evidence, and a mis-parsed id would stage a
 *  price against the wrong position. */
export function parseLegId(handle: string): { id: string; leg: PendingLeg } | null {
  const cut = handle.lastIndexOf('|');
  if (cut <= 0) return null;
  const leg = handle.slice(cut + 1);
  if (leg !== 'sl' && leg !== 'tp') return null;
  return { id: handle.slice(0, cut), leg };
}

/** How many TP legs the modify path could actually move: the PROTECTIVE ones.
 *
 *  ONE PREDICATE WITH THE SERVER, which has already settled this (redteam D3, 2026-08-19,
 *  `manage._is_protective_leg`): a protective leg is an exit that FIRES AT A LEVEL, i.e.
 *  it carries a TRIGGER. A reduce-only scale-out LIMIT does not — and it is a shape Andrei
 *  actually rests. `protection.tp_count` is `hl_account`'s count of every reduce-only leg
 *  ccxt labelled TP, which includes those limits, so it and the server disagree about how
 *  many rungs there are.
 *
 *  IT IS THE LADDER REFUSAL THAT MADE THIS LOAD-BEARING (R4-9). Measured on the live book,
 *  2026-08-21: 7 of 13 positions carry `tp_count > 1` while holding exactly ONE
 *  trigger-bearing TP leg — CC, xyz:AMZN, xyz:BB, xyz:MSFT, xyz:NFLX, xyz:ORCL, xyz:ZHIPU.
 *  On the count, `validateStaged` refuses a TP move on all seven; on the server's own
 *  predicate the move is a legitimate single-leg replace, and `_find_prior_legs` performs
 *  it. Before R4-9 that over-refusal was drag-only and the table button was the escape
 *  hatch; making the check reachable from BOTH paths — the fix — would have made those
 *  seven positions unmanageable from the panel entirely. The same measurement is why the
 *  server's own refusal count went 2 -> 0 when D3 was fixed there.
 *
 *  UNDER-REFUSING IS THE DANGEROUS DIRECTION (D2: position-TPSL ACCUMULATES on this
 *  venue), which is why this aligns with the server rather than relaxing past it — the
 *  binding `_rung_to_replace` refusal still stands behind it, on the same predicate. */
const protectiveTpCount = (p: PositionRow): number =>
  p.protection.legs.filter(
    (l) => l.kind === 'TP' && l.trigger_px !== null && l.trigger_px !== undefined
      && Number.isFinite(l.trigger_px) && l.trigger_px > 0,
  ).length;

const fromRow = (p: PositionRow, prev?: LivePositionLevels): LivePositionLevels => ({
  id: p.coin,
  side: p.side,
  size: p.size,
  mark: p.mark_px,
  entry: p.entry_px,
  liq: p.liq_px,
  // The ORDER GRAPH's view (`protection.*`), never `Position.stop_loss` — that field
  // is a placeholder equal to entry (§9b).
  sl: p.protection.sl,
  tp: p.protection.tp,
  // THE PROTECTIVE COUNT, not `protection.tp_count` — see `protectiveTpCount`: the raw
  // count includes reduce-only scale-out LIMITs, which the server does not consider legs
  // at all, and on the live book that difference refuses a legitimate TP move on 7 of 13
  // positions.
  tpCount: protectiveTpCount(p),
  // A snapshot poll every 15 s must not wipe a stop the user is mid-way through
  // staging, so the pending override SURVIVES a sync.
  pendingSl: prev?.pendingSl ?? null,
  pendingTp: prev?.pendingTp ?? null,
});

export class LiveLevelModel {
  private _byId = new Map<string, LivePositionLevels>();
  private _subs = new Set<() => void>();

  /** Replace the committed prices from an account snapshot. Positions that vanished
   *  are DROPPED with their pending overrides: a staged stop for a closed position is
   *  a price he might act on. */
  sync(snap: Snapshot | null): void {
    const next = new Map<string, LivePositionLevels>();
    for (const p of snap?.positions ?? []) next.set(p.coin, fromRow(p, this._byId.get(p.coin)));
    this._byId = next;
    this._emit();
  }

  get(id: string): LivePositionLevels | null {
    return this._byId.get(id) ?? null;
  }

  ids(): string[] {
    return [...this._byId.keys()];
  }

  /** Stage a price. Returns false when the id is unknown — an unknown id is a
   *  mis-bound renderer, and inventing a record for it is how a level lands on the
   *  wrong position. */
  setPending(id: string, leg: PendingLeg, price: number | null): boolean {
    // Runtime guard, not just the type: `leg` arrives from a DOM dataset in the drag
    // wiring, where TypeScript has already stopped being evidence.
    if (leg !== 'sl' && leg !== 'tp') {
      throw new Error(`livelevels: ${String(leg)} is derived, not an input`);
    }
    const cur = this._byId.get(id);
    if (!cur) return false;
    if (price !== null && !(Number.isFinite(price) && price > 0)) return false;
    this._byId.set(id, { ...cur, [leg === 'sl' ? 'pendingSl' : 'pendingTp']: price });
    this._emit();
    return true;
  }

  /** Discard staged prices (the escape path). */
  clearPending(id: string): void {
    const cur = this._byId.get(id);
    if (!cur || (cur.pendingSl === null && cur.pendingTp === null)) return;
    this._byId.set(id, { ...cur, pendingSl: null, pendingTp: null });
    this._emit();
  }

  /** What should be DRAWN: the staged price if there is one, else the exchange's. */
  effective(id: string): { sl: number | null; tp: number | null; slPending: boolean; tpPending: boolean } | null {
    const p = this._byId.get(id);
    if (!p) return null;
    return {
      sl: p.pendingSl ?? p.sl,
      tp: p.pendingTp ?? p.tp,
      slPending: p.pendingSl !== null,
      tpPending: p.pendingTp !== null,
    };
  }

  /** Called on every change. Returns the unsubscribe. */
  subscribe(fn: () => void): () => void {
    this._subs.add(fn);
    return () => this._subs.delete(fn);
  }

  private _emit(): void {
    for (const fn of this._subs) fn();
  }
}

/** The chart levels for ONE position. A staged price says so on its own plate —
 *  an unmarked pending stop looks like a stop the exchange is holding. */
export function levelsForPosition(m: LiveLevelModel, id: string): AccountLevel[] {
  const p = m.get(id);
  const eff = m.effective(id);
  if (!p || !eff) return [];
  const out: AccountLevel[] = [];
  const push = (price: number | null, color: string, title: string, dashed = true, handle?: string) => {
    if (price !== null && Number.isFinite(price) && price > 0) {
      out.push({ price, color, label: `${title} ${fmtPx(price)}`, dashed, id: handle });
    }
  };
  push(p.entry, LEG_COLORS.entry, `ENTRY ${p.side}`, false);
  // ONLY THESE TWO CARRY A HANDLE, so only these two can be dragged. Entry is history
  // (it already filled) and liquidation is derived from leverage and size — a movable
  // liq line would be a fiction drawn in the colour of a fact.
  push(eff.sl, LEG_COLORS.stop, eff.slPending ? 'SL (staged)' : 'SL', true, legId(id, 'sl'));
  push(eff.tp, LEG_COLORS.tp, eff.tpPending ? 'TP (staged)' : 'TP', true, legId(id, 'tp'));
  push(p.liq, LEG_COLORS.liq, 'LIQ');
  return out;
}

/**
 * IS THE STAGED PRICE A LEVEL THAT CAN EXIST? Returns the refusal SENTENCE, or null.
 *
 * FOUR REFUSALS, and each one describes a level that cannot do its job — not a
 * preference:
 *
 *  * the position is GONE. A stop for a closed position writes to nothing, and the
 *    15 s poll drops the record, so this is the state a user reaches by staging a
 *    price and then being stopped out while the dialog was open;
 *  * a stop on the WRONG SIDE OF THE MARK triggers instantly — it is a market exit
 *    wearing a stop's clothes;
 *  * a stop BEYOND LIQUIDATION never fires: liquidation closes the trade first, so it
 *    is protection that cannot protect (`risk_gates` RULE 5's own arithmetic);
 *  * a target on the wrong side of the mark books a LOSS as a profit — the `xyz:INTC`
 *    incident that RULE 11 exists for.
 *
 * IT NEVER CLAMPS. A clamped stop is a price the user did not choose, drawn in the
 * colour of one they did. The caller reverts the pending value and shows this sentence.
 *
 * THIS IS NOT A SECOND OPINION ON `risk_gates`. It is the PRE-CONFIRM half of the same
 * ordering `manage.py::_modify_sl` uses (rules, then `--confirm`, then write): it decides
 * whether a confirm may even be OFFERED, and the binding refusal still comes from
 * exec_tools on the way to the book. The overlap is deliberate — an invalid level must
 * cost zero network calls (this card), and exec_tools cannot be asked without one.
 */
export function validateStaged(
  model: LiveLevelModel,
  id: string,
  leg: PendingLeg,
  price: number,
): string | null {
  const p = model.get(id);
  if (!p) return `${id} is no longer open — nothing to modify`;
  if (!(Number.isFinite(price) && price > 0)) return `${fmtPx(price)} is not a usable price`;
  const mark = p.mark;
  // Refused rather than waved through: every rule below is a comparison against the
  // mark, so without one there is no check, and "no check" must not read as "passed".
  if (mark === null || !Number.isFinite(mark)) return `no mark price for ${id} — cannot check the level`;
  const long = p.side === 'long';
  if (leg === 'sl') {
    if (long ? price >= mark : price <= mark) {
      return `stop ${fmtPx(price)} is on the wrong side of the mark ${fmtPx(mark)} for a ${p.side} — it would fire immediately`;
    }
    // `liq` may be null (a cross-margin row, or a venue read that did not carry one);
    // no liq = no liq rule, which is honest — the mark rule above still applies.
    if (p.liq !== null && Number.isFinite(p.liq) && (long ? price <= p.liq : price >= p.liq)) {
      return `stop ${fmtPx(price)} is beyond the liquidation price ${fmtPx(p.liq)} — liquidation would close the position first, so the stop could never fire`;
    }
    return null;
  }
  if (long ? price <= mark : price >= mark) {
    return `target ${fmtPx(price)} is on the wrong side of the mark ${fmtPx(mark)} for a ${p.side} — it would book a loss as a profit`;
  }
  // TWO TP LEGS = THE DRAWN LINE IS NOT A LEG (redteam D4, 2026-08-19). `protection.tp`
  // is the MINIMUM of the legs, so with a ladder the line under the cursor is an
  // aggregate; `manage.py::_modify_tp` cancels NOTHING and writes a full-position leg
  // (`_modify_sl` has `_cancel_prior_stop`, the TP path has no equivalent), and
  // position-TPSL ACCUMULATES on this venue
  // (`docs/design/HL_TRIGGER_ORDER_SEMANTICS_2026-08-19.md`). Measured consequence: a
  // third full-size TP appears, reduce-only quantity reaches 2.7x the position, and the
  // dragged line does not move because the minimum leg is unchanged. Refused rather than
  // fixed here: choosing WHICH rung of a ladder to cancel is a money-path decision, and
  // the shell still has `exec_tools.cli.manage modify-tp --size` for a rung.
  if (p.tpCount > 1) {
    return `this position holds ${p.tpCount} take-profit legs and the line you dragged is their aggregate, not one order — moving it would ADD a full-size third target instead of moving a rung. Manage a TP ladder from the shell (exec_tools.cli.manage modify-tp --size)`;
  }
  return null;
}

/** The `/api/trade/modify` call a staged price would make — or why there is none.
 *
 *  ONE shape, so "refused" and "what would be sent" cannot disagree: a refusal has NO
 *  request in it, which is how "an invalid level costs zero network calls" is expressed
 *  in the type rather than in a comment.
 *
 *  `price` IS THE WHOLE PAYLOAD (card t_a23d9cc5). It was a `params` bag keyed `stop` /
 *  `tp`, mirroring the CLI's flags; the route takes one number and derives everything
 *  else from the live position, so the flat field is what the request actually is. */
export type StagedModify =
  | { refusal: string; request?: undefined }
  | { refusal?: undefined; request: { verb: 'modify-sl' | 'modify-tp'; symbol: string; price: number } };

/**
 * WHAT MOUSE-UP DECIDES, as a pure function: validate, then compose the modify request.
 *
 * It exists as its own function because it is the whole money boundary of the drag and
 * `main.ts` cannot be imported by a test (browser entry point). `tpCommitStaged` calls
 * this and then only OPENS the dialog — so the assertions below are about production.
 *
 * `symbol` is the POSITION ID the primitive was bound to, carried through the drag
 * handle — never the hovered row and never the visually nearest line. Andrei runs 26+
 * isolated-margin positions whose stops sit within a few dollars of each other.
 *
 * IT SENDS NOTHING. Composing a request is not making one; the press does that.
 */
export function stagedModifyRequest(
  model: LiveLevelModel,
  id: string,
  leg: PendingLeg,
  price: number,
): StagedModify {
  const refusal = validateStaged(model, id, leg, price);
  if (refusal) return { refusal };
  return {
    request: {
      verb: leg === 'sl' ? 'modify-sl' : 'modify-tp',
      symbol: id,
      price,
    },
  };
}

/** How far the exchange's read-back may sit from the price that was SENT and still AGREE.
 *
 *  TIGHT, because the subject is no longer the price the user TYPED (redteam D6,
 *  2026-08-19). It was 0.001 relative, to absorb the venue rounding a trigger to tick —
 *  but relative-to-price on BTC is a ±$65 blind window, so a $50 stop tighten read
 *  "agrees" against a completely unchanged book. The rounding does not need absorbing at
 *  all: `/api/trade/modify` returns `price`, which IS ccxt's `price_to_precision` output —
 *  the exact string that went to the wire — so the venue either holds that number or the
 *  write did not land. What is left to tolerate is float round-tripping through JSON, not
 *  a tick. */
export const READBACK_TOL_PCT = 1e-9;

/**
 * DID THE EXCHANGE TAKE THE PRICE WE SENT? `committed` is read back from the
 * order graph AFTER the write — never from the CLI's own return value, which has
 * reported `{"cancelled": false, "error": null}` for orders that were already gone
 * (§0b.1). `null` = the order graph shows no such leg at all, which DISAGREES.
 *
 * `sent` must be the SERVER's rounded price (`ModifyRun.price`), never the typed one:
 * see `READBACK_TOL_PCT`.
 */
export const readbackAgrees = (committed: number | null, sent: number): boolean =>
  committed !== null &&
  Number.isFinite(committed) &&
  Math.abs(committed - sent) <= Math.abs(sent) * READBACK_TOL_PCT;

/** THE ORDER THE WRITE ACTUALLY PLACED, as an identity — or `null`.
 *
 * `ModifyRun.placed_order_id` is derived SERVER-SIDE by diffing two `/info` reads
 * around the write (`trade_actions._placed_order_id`), and this is deliberately not
 * computed here from `run.result`: the venue client reports
 * `order.get("id", "unknown")`, and ccxt's `parse_order` yields NO id at all for a
 * trigger leg the venue answers with the bare string `waitingForTrigger` — which is
 * exactly the shape `set_stop_loss` / `set_take_profit` place. Only the server saw
 * both books.
 *
 * `null` (no new trigger, an ambiguous one, or a failed read) is a first-class answer
 * and stays `null`: a read-back that cannot name the order must say so rather than fall
 * back to a `kind` scan, which is the R4-2 defect itself.
 */
export function placedOrderId(run: { placed_order_id: string | null }): string | null {
  const raw = run.placed_order_id;
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s ? s : null;
}

/** The resting leg that IS a given order, matched by identity and by nothing else.
 *
 * THIS REPLACED `legs.find((l) => l.kind === kind)` (R4-2, critical). Matching on
 * `kind` takes the FIRST leg of that kind in the order `/info` happened to return,
 * while the server moves the NEAREST rung (`manage._rung_to_replace` ->
 * `_baseline_leg_price`) and the panel's TP/SL column shows the tightest
 * (`hl_account._protective_legs`). Three implementations of "which leg is it", three
 * answers, and the read-back — the one thing that exists BECAUSE the write's own
 * `confirmed` is a self-report (§0b.1) — was reading whichever leg came first. Measured
 * both ways on a real two-rung ladder: a landed move rendered "READ-BACK DISAGREES",
 * and a FAILED move rendered the calm "what the exchange now reports" over the price
 * the user had typed.
 *
 * `oid` is compared as a string because `/info` returns an int and ccxt returns the
 * decimal string of the same number.
 */
export const legByOrderId = (
  legs: ProtectionLeg[],
  oid: string,
): ProtectionLeg | null => legs.find((l) => l.oid !== null && String(l.oid) === oid) ?? null;

/**
 * WHAT THE EXCHANGE NOW REPORTS, rendered from the RE-READ order graph.
 *
 * THIS IS THE ONLY EVIDENCE THE MOVE-SL/TP DIALOG OFFERS (card t_824b65c5), and it
 * replaces the `exec_tools raw (uv run python -m exec_tools.cli.manage modify-tp
 * --confirm)` block that used to sit under it. A shell command line is not something a
 * trader can act on; the venue's own state is. §0b.1: the write's `confirmed` is a
 * self-report and it has lied, so every field below comes from `pos.protection.legs`
 * (classified by the audited `hl_check.leg_kind`) after `?force=true`.
 *
 * A PRICE THAT CAME BACK ROUNDED IS NORMAL AND LABELLED AS SUCH. The venue rounds a
 * trigger to the asset's tick and the SERVER's ccxt `price_to_precision` is the rounding
 * authority — there is deliberately no client-side rounder here to disagree with it. So
 * `sent` is the server's ROUNDED price (`ModifyRun.price`) and `typed` is what the user
 * asked for; the difference between them is disclosed as rounding, and the venue is held
 * to `sent` EXACTLY. Holding it to `typed` inside a relative tolerance was redteam D6: a
 * ±0.1% window on BTC is ±$65, so a $50 stop move read "agrees" against an unchanged book.
 * A MISSING leg, or one that is not the price we sent, is a real disagreement and is loud.
 *
 * `size === 0` is HL's exchange-managed full-position TPSL marker (`isPositionTpsl` with
 * `sz=0`), so it prints as "full position" — naming it "0" states the opposite.
 *
 * THE LEG IS FOUND BY ORDER IDENTITY, NEVER BY `kind` (R4-2, critical, fixed
 * 2026-08-21). `placedOid` is the id the SERVER's own write returned
 * (`placedOrderId(run)`), so this reads back the ONE order the write created and no
 * other. `kind` still labels the rows, because a leg's own `kind` is the audited
 * `hl_check.leg_kind` and this function forms no second opinion about it.
 *
 * `placedOid === null` — the client returned no usable id — is its OWN loud state, not a
 * fallback to a `kind` scan: an unidentifiable write is exactly the case where the first
 * leg of that kind is most likely to be the wrong one.
 *
 * Lives here rather than in `tradepanel.ts` so it shares ONE tolerance with
 * `readbackAgrees` above: two numbers for "did the venue take our price" is how the chart
 * and the dialog end up disagreeing about the same stop.
 */
export function renderReadback(
  kind: 'SL' | 'TP',
  typed: number,
  pos: { side: string; protection: { legs: ProtectionLeg[] } } | null,
  sent: number,
  placedOid: string | null,
): string {
  if (placedOid === null) {
    return `<div class="tp-note bad">READ-BACK IMPOSSIBLE: the write did not report which
      order it created, so the resting ${kind} cannot be matched to it. Nothing here is a
      confirmation — read the order graph before acting.</div>`;
  }
  const leg = legByOrderId(pos?.protection.legs ?? [], placedOid);
  const px = leg ? (leg.trigger_px ?? leg.limit_px) : null;
  if (!leg || px === null || px === undefined || !Number.isFinite(px)) {
    return `<div class="tp-note bad">READ-BACK DISAGREES: the exchange is NOT resting the
      ${kind} order this dialog placed (${esc(placedOid)}). The book was not changed the
      way this dialog asked — check the order graph before acting. Nothing here is a
      confirmation.</div>`;
  }
  // A CONTRADICTION IS NOT SILENTLY LABELLED. The rows below are titled with the `kind`
  // this dialog moved; if the order the write placed is classified as the OTHER leg by
  // `hl_check.leg_kind`, then one of the two is wrong about what is protecting this
  // position, and printing our own title over the venue's row would hide exactly that.
  if (leg.kind !== kind) {
    return `<div class="tp-note bad">READ-BACK DISAGREES: this dialog moved the ${kind},
      but the order it placed (${esc(placedOid)}) is resting as a ${esc(leg.kind)} on the
      exchange. Read the order graph before acting — nothing here is a
      confirmation.</div>`;
  }
  const agrees = readbackAgrees(px, sent);
  const rounded =
    agrees && px !== typed
      ? ` <span class="muted">(rounded from ${fmtPx(typed)} to the venue's tick)</span>`
      : '';
  const rows: [string, string][] = [
    [`${kind} trigger`, `${fmtPx(px)}${rounded}`],
    ['size', leg.size === null || leg.size === 0 ? 'full position' : String(leg.size)],
    ['side', pos?.side ?? '—'],
    ['reduce only', leg.is_position_tpsl ? 'yes (exchange-managed)' : 'yes'],
  ];
  const kv = rows.map(([k, v]) => `<span>${k}</span><span>${v}</span>`).join('');
  const head = agrees
    ? '<div class="tp-note ok">WHAT THE EXCHANGE NOW REPORTS (read back from /info):</div>'
    : `<div class="tp-note bad">READ-BACK DISAGREES: the price sent was ${fmtPx(sent)}, the
        exchange reports ${fmtPx(px)}. What is shown is what the exchange reports, NOT what
        was sent.</div>`;
  return `${head}<div class="tp-kv">${kv}</div>`;
}

/**
 * WHAT THE CONFIRM DIALOG SAYS, and every field is required by the card: the POSITION
 * ID (not the symbol alone — `AMZN` and `xyz:AMZN` are two different books), the side,
 * which leg, and old -> new. A dialog that says only "move stop to 273" cannot be
 * checked by the person pressing it against the position they meant.
 *
 * A pure function so a test can read the sentence without a DOM — the dialog is the
 * last thing between a gesture and a real stop.
 */
export function commitSummary(p: LivePositionLevels, leg: PendingLeg, price: number): string {
  const old = leg === 'sl' ? p.sl : p.tp;
  return (
    `${p.id} ${p.side} ${p.size} — ${leg.toUpperCase()} ` +
    `${old === null ? 'none' : fmtPx(old)} -> ${fmtPx(price)}`
  );
}

/**
 * The drag callbacks for ONE position: stage the dragged price, refuse anything else.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: write, or clamp. A drag is a gesture, not an
 * intent (`ticket.ts:251`).
 *
 * `onCommit` (optional) is called on MOUSE-UP and it does not write either — it ASKS,
 * by opening the existing confirm dialog. Validation runs there, before the dialog is
 * offered, and the write still needs the explicit press: mouse-up is the moment a user
 * has finished choosing a price, which is exactly when the question "send this?" is
 * worth asking, and never the moment to answer it for them.
 *
 * The handle is parsed, and a handle for another position is IGNORED rather than
 * coerced: this callback set belongs to a bound id, and honouring a foreign handle is
 * how one pane's drag lands on another position's stop.
 */
export function dragCallbacks(
  model: LiveLevelModel,
  id?: string,
  onCommit?: (id: string, leg: PendingLeg, price: number) => void,
): LevelDragCallbacks {
  const mine = (handle: string) => {
    const parsed = parseLegId(handle);
    if (!parsed) return null;
    // WITH an id: this callback set belongs to one bound position, so a foreign
    // handle is IGNORED rather than coerced — honouring it is how one pane's drag
    // lands on another position's stop.
    // WITHOUT one (the per-pane wiring in `main.ts`): the handle itself names the
    // position, which is exactly why it carries the id. Still never "nearest line".
    return id !== undefined && parsed.id !== id ? null : parsed;
  };
  return {
    onDrag: (handle, price) => {
      const parsed = mine(handle);
      if (parsed) model.setPending(parsed.id, parsed.leg, price);
    },
    onDrop: (handle, price) => {
      const parsed = mine(handle);
      if (parsed) onCommit?.(parsed.id, parsed.leg, price);
    },
  };
}

/**
 * Attach drag handles — and any STAGED price — to levels already built for one market.
 *
 * WHY THIS RATHER THAN DRAWING FROM `levelsForPosition`: the panel's `levelsFor` emits
 * every protective leg the order graph holds (a position may carry two TP legs) plus the
 * resting orders, with its own measured price-dedupe. The model holds the position's SL
 * and TP as single prices. Rendering from the model would therefore DROP lines the chart
 * shows today, and a missing protective leg is the one misread this app must not produce.
 * So the panel keeps drawing, and this adds the interaction on top.
 *
 * MATCHING IS BY PRICE AGAINST THE COMMITTED VALUE, not by label text: `protection.sl` is
 * where the model's committed stop came from, so the leg that equals it is the leg the
 * model speaks for. A second SL leg at another price keeps its line and stays read-only,
 * which is honest — the model has no opinion about it.
 *
 * BUT A PRICE COLLISION IS NOT A MATCH (redteam D3, 2026-08-19). `levelsFor` dedupes by
 * price, first-writer-wins, and its first pass pushes ENTRY then LIQ. So on a BREAKEVEN
 * stop (`protection.sl === entry_px`, which `manage.py` has a whole `breakeven` verb for)
 * the SL line was never emitted and the `|sl` handle landed on the ENTRY line — dragging
 * it rewrote the entry's price and label, so the position's real entry was drawn nowhere.
 * The same shape put the handle on the LIQ line when the stop sat at liquidation, which
 * defeats "liquidation is not draggable" through the matcher rather than through the
 * model. A handle is therefore only minted on a line whose LABEL says it is that leg —
 * `levelsFor` writes `SL <size>` / `TP <size>` for protective legs and `ENTRY <side>` /
 * `LIQ` for the other two, so the two facts must agree before the line becomes grabbable.
 * When they do not, the leg falls through to the staged-push below and gets its own line.
 *
 * A leg with NO committed line still draws (a position with no stop at all is exactly when
 * one gets dragged in), and it says `(staged)` IF AND ONLY IF that leg is actually pending.
 * Both directions are actionable: an unmarked pending stop looks like a stop the exchange
 * is holding, and a stop the exchange IS holding marked `(staged)` invites placing a second
 * one (redteam round 1, 2026-08-19 -- the push branch used to label unconditionally).
 */
export function withDragHandles(
  levels: readonly AccountLevel[],
  model: LiveLevelModel,
  id: string,
): AccountLevel[] {
  const p = model.get(id);
  const eff = model.effective(id);
  if (!p || !eff) return [...levels];
  const same = (a: number, b: number | null) => b !== null && Math.abs(a - b) < 1e-9;
  // The line's own label must name the leg. See the docstring: `levelsFor` writes
  // `SL 0.005` / `TP (Limit) 0.005`, and `ENTRY short` / `LIQ` for the two that must
  // never become grabbable.
  const labelled = (label: string, leg: PendingLeg) =>
    new RegExp(`^${leg.toUpperCase()}\\b`).test(label);
  const seen = { sl: false, tp: false };
  const out = levels.map((lv): AccountLevel => {
    for (const leg of ['sl', 'tp'] as const) {
      if (seen[leg] || !same(lv.price, p[leg]) || !labelled(lv.label, leg)) continue;
      seen[leg] = true;
      const staged = leg === 'sl' ? eff.slPending : eff.tpPending;
      const price = (leg === 'sl' ? eff.sl : eff.tp) ?? lv.price;
      return {
        ...lv,
        price,
        // The label is only rewritten when the price is NOT the exchange's any more —
        // otherwise the panel's own text (which carries the leg's size) is left alone.
        label: staged ? `${leg.toUpperCase()} (staged) ${fmtPx(price)}` : lv.label,
        id: legId(id, leg),
      };
    }
    return lv;
  });
  for (const leg of ['sl', 'tp'] as const) {
    const price = leg === 'sl' ? eff.sl : eff.tp;
    if (seen[leg] || price === null) continue;
    // `(staged)` is a claim about the BOOK, not about which branch drew the line. A leg
    // reaches this push whenever no line named it -- a breakeven stop, or manage mode where
    // `drawn` is empty -- and in both cases the exchange may well be holding that exact
    // price. Labelling it staged is D1's defect with the sign flipped: a price the venue
    // HAS, wearing the mark that means not-sent-yet, which invites placing a duplicate stop.
    const staged = leg === 'sl' ? eff.slPending : eff.tpPending;
    out.push({
      price,
      color: leg === 'sl' ? LEG_COLORS.stop : LEG_COLORS.tp,
      label: `${leg.toUpperCase()}${staged ? ' (staged)' : ''} ${fmtPx(price)}`,
      dashed: true,
      id: legId(id, leg),
    });
  }
  return out;
}

/**
 * WHAT THE CONTEXT PANES (1W / 1D) DRAW: the live position's lines AND the pre-trade
 * ticket's planned legs, in ONE array — never one INSTEAD of the other.
 *
 * THIS IS THE FIX FOR A CRITICAL DEFECT (redteam D1, 2026-08-19). `main.ts` used to choose
 * between them: `if (tkHoldsPrices) tkDrawLines(); else drawContextLevels(withHandles)`.
 * Pressing `+ Trade` seeds an entry from the mark, so ANY ticket price diverted 1W/1D to
 * `ticketGeometry`. Measured in Chromium on a live BTC short: the 15m pane drew the
 * exchange's stop at 65,602 while 1W and 1D drew a red dashed line labelled `SL` at
 * 61,009.86 — 7% and $4,592 of stop distance apart, with nothing on screen saying which
 * one the exchange was holding. With only an entry seeded, the live stop vanished from
 * 1W/1D entirely. Both readings are one failure: a pane showing a price the position does
 * not hold, in the colour of one it does.
 *
 * A UNION IS SAFE HERE AND SUBSTITUTION WAS NOT, for a structural reason: each context
 * pane owns exactly ONE `levels` primitive, so both sets pass through one collision list.
 * The overprinting the if/else was written to avoid is a two-PRIMITIVE problem, and it
 * belongs to the execution pane (ticket legs + `levels`) — which is why
 * `drawContextLevels` still skips it.
 *
 * THE PLAN IS PREFIXED. Two lines labelled `SL` at two prices is exactly what made D1
 * dangerous rather than merely wrong; a planned stop and a resting stop must be
 * distinguishable without reading a price.
 *
 * AND THE PLAN IS NOT DRAGGABLE (no `id`): the ticket's own primitive owns that gesture on
 * the execution pane, and a second draggable copy of one planned stop is the same
 * two-sources-of-truth bug one level down.
 *
 * A PURE FUNCTION, deliberately: this decision lived in `main.ts` and its only guard was a
 * substring grep over the function's source, which cannot see WHICH prices cross a branch
 * — that is how D1 shipped green.
 */
export function mergeContextLevels(
  live: readonly AccountLevel[],
  planLegs: readonly { price: number; color: string; label: string }[],
): AccountLevel[] {
  return [
    ...live,
    ...planLegs.map((leg) => ({
      price: leg.price,
      color: leg.color,
      label: `PLAN ${leg.label}`,
      dashed: true,
    })),
  ];
}

/** Bind one pane's EXISTING primitive to one position id — render AND drag.
 *
 *  `LevelPrimitive` is reused, not forked, and nothing is constructed here: the pane
 *  already owns one (`chartpane.ts:560`). Draws immediately, then on every model
 *  change — so a drag on ANY pane repaints every other pane in the same tick, because
 *  they all subscribe to the one model rather than syncing copies.
 *
 *  `setCallbacks` is optional so the existing read-only callers keep compiling and keep
 *  behaving: a primitive whose callbacks are never set has no draggable legs at all
 *  (`levelprimitive.ts` returns an empty leg list when `_cb` is null).
 *
 *  Returns the unbind: it drops the subscription AND the callbacks, because a pane left
 *  draggable after its position is gone would stage a price on a position that closed. */
export function bindLevels(
  primitive: {
    setData(levels: AccountLevel[]): void;
    setCallbacks?(cb: LevelDragCallbacks | null): void;
  },
  model: LiveLevelModel,
  id: string,
): () => void {
  const draw = () => primitive.setData(levelsForPosition(model, id));
  draw();
  primitive.setCallbacks?.(dragCallbacks(model, id));
  const off = model.subscribe(draw);
  return () => {
    off();
    primitive.setCallbacks?.(null);
  };
}
